import { Link } from "react-router-dom";
import { Truck, Clock, Package, MapPin, ShieldCheck } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function Delivery() {
  return (
    <>
      <SEOHead
        title="Delivery Information | Samboards"
        description="UK delivery information for Samboards sensory products. Fast processing, tracked shipping, and transparent delivery times."
        canonical="/delivery"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Delivery", url: "/delivery" }
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />
        
        <Page>
          <div className="max-w-4xl mx-auto py-8 sm:py-12">
            <header className="text-center mb-10 sm:mb-14">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-300/80 mb-3">
                Delivery
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent mb-4">
                Delivery Information
              </h1>
              <p className="text-slate-300 max-w-2xl mx-auto">
                We're committed to getting your sensory products to you quickly and safely.
              </p>
            </header>

            <div className="space-y-8">
              {/* Processing Time */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <Clock className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">Processing Time</h2>
                    <p className="text-slate-300 leading-relaxed">
                      Orders are typically processed within <strong className="text-emerald-200">1–3 business days</strong>. 
                      During busy periods, this may extend slightly. You'll receive an email confirmation 
                      when your order has been dispatched.
                    </p>
                  </div>
                </div>
              </section>

              {/* UK Delivery */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <Truck className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">UK Delivery</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      We deliver across the United Kingdom. Standard UK delivery typically takes 
                      <strong className="text-emerald-200"> 3–5 business days</strong> after dispatch.
                    </p>
                    <ul className="space-y-2 text-slate-300">
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        Mainland UK: 3–5 business days
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        Scottish Highlands & Islands: 5–7 business days
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        Northern Ireland: 5–7 business days
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Shipping Costs */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <Package className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">Shipping Costs</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Shipping costs vary depending on the size and weight of your order. 
                      The exact cost will be calculated and shown at checkout before you complete your purchase.
                    </p>
                    <div className="rounded-xl bg-slate-800/50 border border-slate-700/50 p-4">
                      <p className="text-sm text-slate-400">
                        <strong className="text-emerald-200">Typical UK shipping:</strong> £3.95 – £7.95 depending on order size.
                        Larger education kits and bulk orders may have different rates.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Tracking */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <MapPin className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">Order Tracking</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Once your order is dispatched, you'll receive a tracking number via email. 
                      You can track your delivery status using our{" "}
                      <Link to="/order-tracking" className="text-emerald-300 hover:text-emerald-200 underline">
                        order tracking page
                      </Link>.
                    </p>
                    <p className="text-slate-400 text-sm">
                      Please allow up to 24 hours for tracking information to become active after dispatch.
                    </p>
                  </div>
                </div>
              </section>

              {/* Secure Packaging */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <ShieldCheck className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">Secure Packaging</h2>
                    <p className="text-slate-300 leading-relaxed">
                      All products are carefully packaged to ensure they arrive in perfect condition. 
                      We use eco-friendly packaging materials wherever possible.
                    </p>
                  </div>
                </div>
              </section>

              {/* Contact */}
              <section className="rounded-2xl border border-emerald-500/30 bg-emerald-900/20 p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-emerald-100 mb-3">Questions About Delivery?</h2>
                <p className="text-slate-300 mb-4">
                  If you have any questions about your delivery or need to make special arrangements, 
                  please don't hesitate to get in touch.
                </p>
                <p className="text-slate-300">
                  Email:{" "}
                  <a href="mailto:samboards19@aol.com" className="text-emerald-300 hover:text-emerald-200 underline">
                    samboards19@aol.com
                  </a>
                </p>
              </section>
            </div>
          </div>
        </Page>
      </div>
    </>
  );
}
